import { Alert, Stack } from "@mantine/core";
import { useEffect, useRef } from "react";

import type { SiteOut, StatusOut } from "../api/generated/model";
import { IconAlertOctagon, IconAlertTriangle, IconDatabase } from "../components/icons";
import { PromptGate, playPrompt, type SweepState, sweepState } from "../lib/prompt";
import { formatAge, formatLocal } from "../lib/time";

/** "Sweep due / overdue" (with the sound prompt, once per episode) and "DB unavailable". */
export function Banners({
  status,
  site,
  sound,
  play = playPrompt,
}: {
  status: StatusOut | undefined;
  site: SiteOut | undefined;
  sound: boolean;
  play?: () => void;
}) {
  const gate = useRef(new PromptGate());
  const state: SweepState =
    status && site ? sweepState(status.last_complete_sweep_age_s, site.sweep) : "unknown";
  const episode = status?.last_complete_sweep ?? null;

  useEffect(() => {
    if (sound && gate.current.shouldPrompt(state, episode)) play();
  }, [sound, state, episode, play]);

  if (!status) return null;
  const tz = site?.timezone ?? "UTC";
  const last = status.last_complete_sweep
    ? `the last complete sweep ended ${formatLocal(status.last_complete_sweep, tz)} (${formatAge(
        status.last_complete_sweep_age_s ?? 0,
      )} ago)`
    : "";
  return (
    <Stack gap="xs" mb="lg">
      {!status.db && (
        <Alert color="orange" variant="light" title="Database unavailable" icon={<IconDatabase size={20} />} data-testid="banner-db">
          The numbers cannot be shown right now. Recording continues: sweeps are kept and processed
          when the database is back.
        </Alert>
      )}
      {state === "overdue" && (
        <Alert color="red" variant="light" title="Sweep overdue" icon={<IconAlertOctagon size={20} />} data-testid="banner-overdue">
          A sweep is overdue: {last}. Please sweep the stockpile.
        </Alert>
      )}
      {state === "due" && (
        <Alert color="yellow" variant="light" title="Sweep due" icon={<IconAlertTriangle size={20} />} data-testid="banner-due">
          A sweep is due: {last}.
        </Alert>
      )}
    </Stack>
  );
}
